import React, { useState } from 'react';
import { Screen, AppState } from '../App';
import * as authApi from '../authApi';
import PasswordInput from '../components/PasswordInput';
import Icon from '../components/Icon';
import { isValidEmail, suggestEmailFix } from '../util/email';

// A calm, one-question-at-a-time signup. Same backend contract as before
// (authApi.signup + migrateLocalData + refreshUser, then paywall/onboarding).
// Parent Mode adds a first "I am a…" step: a Teacher continues through the
// existing school questions; a Parent skips them entirely (role is stored as
// 'parent', and no last name / DOB / school is ever asked about the child).

interface Props {
  navigate: (s: Screen, state?: AppState) => void;
  refreshUser: () => Promise<any>;
  appState?: AppState;
  compact?: boolean;          // true when rendered inside the laptop screen
  onExit?: () => void;        // "Back" out of the first step (e.g. return to landing)
}

const ROLES = [
  { value: 'classroom_teacher', label: 'Classroom teacher' },
  { value: 'special_educator', label: 'Special educator' },
  { value: 'school_counselor', label: 'School counselor' },
  { value: 'administrator', label: 'Administrator' },
  { value: 'paraprofessional', label: 'Paraprofessional' },
  { value: 'other', label: 'Other' },
];
const GRADE_BANDS = [
  { value: 'prek_2', label: 'Pre-K–2' },
  { value: '3_5', label: 'Grades 3–5' },
  { value: 'middle', label: 'Middle school' },
  { value: 'high', label: 'High school' },
  { value: 'multiple', label: 'Multiple grade levels' },
];

// Step keys, so the flow can differ by account type without brittle index math.
type StepKey = 'accountType' | 'name' | 'email' | 'password' | 'role' | 'gradeBand' | 'school' | 'joinCode';

export default function SignupWizard({ navigate, refreshUser, appState, compact, onExit }: Props) {
  const wantsToSubscribe = appState?.intent === 'subscribe';
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({ accountType: appState?.accountType || '', firstName: '', lastName: '', email: '', password: '', role: '', gradeBand: '', school: '', joinCode: '' });
  const [error, setError] = useState('');
  const [typoWarn, setTypoWarn] = useState('');
  const [loading, setLoading] = useState(false);

  const set = (k: keyof typeof form, v: string) => setForm(f => ({ ...f, [k]: v }));

  // The parent flow skips the school-staff questions entirely.
  const isParent = form.accountType === 'parent';
  const stepKeys: StepKey[] = isParent
    ? ['accountType', 'name', 'email', 'password']
    : ['accountType', 'name', 'email', 'password', 'role', 'gradeBand', 'school', 'joinCode'];
  const totalSteps = stepKeys.length;
  const key = stepKeys[step];

  const submit = async (emailOverride?: string) => {
    setLoading(true); setError('');
    try {
      // Parents store role='parent' (the account-type sentinel). Teachers store
      // their professional sub-role from the 'role' step.
      const payload = { ...form, email: emailOverride || form.email, role: isParent ? 'parent' : form.role };
      await authApi.signup(payload);
      await authApi.migrateLocalData();
      await refreshUser();
      navigate(wantsToSubscribe ? 'paywall' : 'onboarding');
    } catch (err: any) {
      setError(err?.message || 'Could not create your account. Please try again.');
      setLoading(false);
    }
  };

  const validate = (): string => {
    if (key === 'accountType') { if (!form.accountType) return 'Please choose one to continue.'; }
    if (key === 'name') { if (!form.firstName.trim() || !form.lastName.trim()) return 'Please enter your first and last name.'; }
    if (key === 'email') { if (!isValidEmail(form.email)) return 'Please enter a valid email address.'; }
    if (key === 'password') { if (form.password.length < 8) return 'Password must be at least 8 characters.'; }
    return ''; // role / grade band / school / join code are optional
  };

  const next = () => {
    const v = validate();
    if (v) { setError(v); return; }
    setError('');
    if (key === 'email') {
      const s = suggestEmailFix(form.email);
      if (s && s !== typoWarn) { setTypoWarn(s); return; }
    }
    if (step === totalSteps - 1) { submit(); return; }
    setStep(s => Math.min(totalSteps - 1, s + 1));
  };

  const back = () => {
    setError(''); setTypoWarn('');
    if (step === 0) { onExit ? onExit() : navigate('landing'); return; }
    setStep(s => s - 1);
  };

  const onEnterKey = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); next(); }
  };

  const QUESTIONS: Record<StepKey, string> = {
    accountType: 'I am a…',
    name: 'What should we call you?',
    email: 'What is your email?',
    password: 'Create your password.',
    role: 'What is your role?',
    gradeBand: 'Which students do you primarily support?',
    school: 'Where do you work?',
    joinCode: 'Did your school give you a join code?',
  };

  const OptionList = ({ options, selected, onSelect }: { options: { value: string; label: string; desc?: string }[]; selected: string; onSelect: (v: string) => void }) => (
    <div role="radiogroup" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {options.map(o => {
        const on = selected === o.value;
        return (
          <button
            key={o.value} type="button" role="radio" aria-checked={on}
            onClick={() => onSelect(o.value)}
            style={{
              textAlign: 'left', padding: o.desc ? '16px' : '13px 16px', borderRadius: 'var(--radius-sm)',
              border: `1.5px solid ${on ? 'var(--teal)' : '#e0dbd3'}`,
              background: on ? 'var(--teal-light)' : 'var(--white)',
              color: on ? 'var(--teal)' : 'var(--charcoal)', fontWeight: on ? 700 : 500,
              fontSize: 15, display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            }}
          >
            <span>
              {o.label}
              {o.desc && <span style={{ display: 'block', fontSize: 13, fontWeight: 400, color: 'var(--text-secondary)', marginTop: 2 }}>{o.desc}</span>}
            </span>
            {on && <Icon name="check-circle" size={18} />}
          </button>
        );
      })}
    </div>
  );

  const stepBody = () => {
    switch (key) {
      case 'accountType':
        return <OptionList
          options={[
            { value: 'teacher', label: '👩‍🏫 Teacher', desc: 'I support students in a school setting' },
            { value: 'parent', label: '👪 Parent', desc: 'I want to understand and support my own child' },
          ]}
          selected={form.accountType}
          onSelect={v => set('accountType', v)}
        />;
      case 'name':
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div className="form-group" style={{ margin: 0 }}><label>First name</label><input autoFocus value={form.firstName} onChange={e => set('firstName', e.target.value)} onKeyDown={onEnterKey} placeholder="Sarah" /></div>
            <div className="form-group" style={{ margin: 0 }}><label>Last name</label><input value={form.lastName} onChange={e => set('lastName', e.target.value)} onKeyDown={onEnterKey} placeholder="Johnson" /></div>
            <p style={{ fontSize: 12.5, color: 'var(--text-secondary)', lineHeight: 1.55, background: 'var(--teal-light)', borderRadius: 8, padding: '10px 12px' }}>
              Your trial is completely free and does not renew automatically. After seven days, choose $29/month or $299/year to continue.
            </p>
          </div>
        );
      case 'email':
        return (
          <div className="form-group" style={{ margin: 0 }}>
            <label>Email address</label>
            <input type="email" autoFocus value={form.email} onChange={e => { set('email', e.target.value); setTypoWarn(''); }} onKeyDown={onEnterKey} placeholder="sarah@example.com" />
            {typoWarn && (
              <div style={{ background: 'var(--amber-light)', border: '1px solid var(--amber)', borderRadius: 8, padding: '10px 12px', marginTop: 8, fontSize: 13, lineHeight: 1.5 }}>
                <p style={{ color: '#b87800', fontWeight: 600, marginBottom: 6 }}>Did you mean <strong>{typoWarn}</strong>?</p>
                <p style={{ color: 'var(--text-secondary)', marginBottom: 8 }}>A wrong address means you'll never get your welcome or reset emails.</p>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <button type="button" onClick={() => { set('email', typoWarn); setTypoWarn(''); setStep(s => s + 1); }} className="btn-small">Use {typoWarn}</button>
                  <button type="button" onClick={() => { setTypoWarn(''); setStep(s => s + 1); }} style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', fontSize: 13, textDecoration: 'underline', cursor: 'pointer' }}>No, mine is correct</button>
                </div>
              </div>
            )}
          </div>
        );
      case 'password':
        return (
          <div className="form-group" style={{ margin: 0 }}>
            <label>Password</label>
            <PasswordInput value={form.password} onChange={e => set('password', e.target.value)} placeholder="At least 8 characters" autoComplete="new-password" />
            <p className="form-hint">At least 8 characters. Tap “Show” to check it.</p>
          </div>
        );
      case 'role':
        return <OptionList options={ROLES} selected={form.role} onSelect={v => set('role', v)} />;
      case 'gradeBand':
        return <OptionList options={GRADE_BANDS} selected={form.gradeBand} onSelect={v => set('gradeBand', v)} />;
      case 'school':
        return (
          <div className="form-group" style={{ margin: 0 }}>
            <label>School <span style={{ fontWeight: 400, color: 'var(--text-secondary)' }}>(optional)</span></label>
            <input autoFocus value={form.school} onChange={e => set('school', e.target.value)} onKeyDown={onEnterKey} placeholder="Lincoln Elementary" />
          </div>
        );
      case 'joinCode':
        return (
          <div className="form-group" style={{ margin: 0 }}>
            <label>School join code <span style={{ fontWeight: 400, color: 'var(--text-secondary)' }}>(optional)</span></label>
            <input autoFocus value={form.joinCode} onChange={e => set('joinCode', e.target.value.toUpperCase())} onKeyDown={onEnterKey} placeholder="If your school gave you one" style={{ textTransform: 'uppercase' }} />
            <p className="form-hint">If your school covers Decode My Student, your code unlocks school-covered access and skips the trial.</p>
          </div>
        );
      default: return null;
    }
  };

  const pct = Math.round(((step + 1) / totalSteps) * 100);
  const isLast = step === totalSteps - 1;

  return (
    <div className={compact ? 'wizard wizard-compact' : 'wizard'}>
      {/* Progress */}
      <div style={{ marginBottom: compact ? 12 : 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--teal)' }}>Step {step + 1} of {totalSteps}</span>
          <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{wantsToSubscribe ? 'Then choose your plan' : '7 days free · no card'}</span>
        </div>
        <div style={{ height: 5, background: '#eae4da', borderRadius: 3, overflow: 'hidden' }}>
          <div style={{ height: '100%', width: `${pct}%`, background: 'var(--teal)', borderRadius: 3, transition: 'width 0.3s ease' }} />
        </div>
      </div>

      <h2 style={{ fontSize: compact ? 18 : 22, marginBottom: compact ? 10 : 16 }}>{QUESTIONS[key]}</h2>

      {stepBody()}

      {error && <div style={{ background: 'var(--coral-light)', color: 'var(--coral)', padding: '10px 14px', borderRadius: 8, fontSize: 14, marginTop: 14 }}>{error}</div>}

      {/* Concise trial summary + legal acceptance at the final step. */}
      {isLast && (
        <div style={{ background: 'var(--teal-light)', borderRadius: 10, padding: '12px 14px', marginTop: 16 }}>
          {['7-day free trial', 'No credit card required', 'Does not renew automatically', 'Continue afterward for $29/month or $299/year'].map(line => (
            <div key={line} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 4 }}>
              <span style={{ color: 'var(--teal)', fontWeight: 700, flexShrink: 0 }}>✓</span>
              <span style={{ fontSize: 13, color: 'var(--charcoal)' }}>{line}</span>
            </div>
          ))}
        </div>
      )}
      {isLast && (
        <p style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.6, marginTop: 12 }}>
          By creating an account you agree to our{' '}
          <span onClick={() => navigate('terms')} style={{ color: 'var(--teal)', fontWeight: 600, cursor: 'pointer', textDecoration: 'underline' }}>Terms of Service</span>{' '}
          and{' '}
          <span onClick={() => navigate('privacy')} style={{ color: 'var(--teal)', fontWeight: 600, cursor: 'pointer', textDecoration: 'underline' }}>Privacy Policy</span>.{isParent ? ' We use first names only — your child\'s privacy is always protected.' : ' We use initials only — student privacy is always protected.'}
        </p>
      )}

      <div style={{ display: 'flex', gap: 10, marginTop: 18 }}>
        <button type="button" onClick={back} className="btn-secondary" style={{ flex: '0 0 auto', width: 'auto', padding: '13px 20px' }} disabled={loading}>Back</button>
        <button type="button" onClick={next} className="btn-primary" style={{ flex: 1 }} disabled={loading}>
          {loading ? 'Creating your account…' : isLast ? (wantsToSubscribe ? 'Create account & choose plan' : 'Create my account') : 'Continue'}
        </button>
      </div>

      {step === 0 && (
        <p style={{ textAlign: 'center', fontSize: 13, color: 'var(--text-secondary)', marginTop: 14 }}>
          Already have an account? <span onClick={() => navigate('login')} style={{ color: 'var(--teal)', fontWeight: 600, cursor: 'pointer' }}>Log in</span>
        </p>
      )}
    </div>
  );
}
